import { HYPERLIQUID_WS_URL } from '@/constants/hyperliquid'

type SubKey = string

type Listener = (msg: unknown) => void

type SubscriptionEntry = {
	count: number
	listeners: Set<Listener>
	subscription: object
}

/**
 * Singleton WebSocket client for Hyperliquid. Multiplexes multiple
 * subscriptions on one connection. Auto-reconnects with 2s linear backoff
 * and re-subscribes everything on reconnect.
 *
 * Subscription channels we use:
 *   - l2Book   per outcome coin   (every visible market)
 *   - userEvents per primary addr (once when authenticated)
 *
 * Refcounting on `subscribe()` returns an unsubscribe fn the caller MUST
 * call on unmount — when count reaches 0 we send unsubscribe to the server.
 */
// Heartbeat: HL doesn't notify clients of dead connections, so we send our own
// ping when idle and force-close if no response. Without this, a TCP keepalive
// timeout (30s–5min depending on OS) can leave the UI showing "connected"
// status with a stale orderbook — and users place orders against old mids.
const HEARTBEAT_TICK_MS = 10_000
const HEARTBEAT_PING_AFTER_IDLE_MS = 25_000
const HEARTBEAT_FORCE_CLOSE_AFTER_IDLE_MS = 50_000

class HyperliquidWS {
	private ws: WebSocket | null = null
	private subs = new Map<SubKey, SubscriptionEntry>()
	private connectPromise: Promise<void> | null = null
	private listeners = new Set<(status: WSStatus) => void>()
	private currentStatus: WSStatus = 'disconnected'
	private heartbeatTimer: ReturnType<typeof setInterval> | null = null
	private lastMessageAt = 0
	private reconnectAttempt = 0

	getStatus(): WSStatus {
		return this.currentStatus
	}

	onStatus(cb: (status: WSStatus) => void): () => void {
		this.listeners.add(cb)
		cb(this.currentStatus)
		return () => this.listeners.delete(cb)
	}

	private setStatus(s: WSStatus) {
		this.currentStatus = s
		for (const cb of this.listeners) cb(s)
	}

	private connect(): Promise<void> {
		if (this.connectPromise) return this.connectPromise
		this.setStatus('connecting')
		this.connectPromise = new Promise((resolve, reject) => {
			const ws = new WebSocket(HYPERLIQUID_WS_URL)
			ws.onopen = () => {
				this.ws = ws
				this.lastMessageAt = Date.now()
				this.reconnectAttempt = 0
				this.startHeartbeat()
				this.setStatus('connected')
				for (const sub of this.subs.values()) {
					ws.send(JSON.stringify({ method: 'subscribe', subscription: sub.subscription }))
				}
				resolve()
			}
			ws.onerror = () => {
				this.setStatus('reconnecting')
				reject(new Error('ws error'))
			}
			ws.onclose = () => {
				this.ws = null
				this.connectPromise = null
				this.stopHeartbeat()
				this.setStatus('reconnecting')
				const delay = Math.min(2_000 * 2 ** this.reconnectAttempt, 60_000)
				const jitter = Math.random() * Math.min(delay * 0.3, 5_000)
				this.reconnectAttempt++
				setTimeout(() => {
					this.connect().catch(() => {})
				}, delay + jitter)
			}
			ws.onmessage = ev => {
				this.lastMessageAt = Date.now()
				try {
					const msg = JSON.parse(ev.data as string) as {
						channel?: string
						data?: { coin?: string; user?: string }
					}
					const channel = msg.channel
					if (!channel) return
					// HL responds to our heartbeat pings on the `pong` channel.
					// Receipt is already reflected in lastMessageAt above; no
					// further routing needed.
					if (channel === 'pong') return
					for (const entry of this.subs.values()) {
						const sub = entry.subscription as { type: string; coin?: string; user?: string }
						// Hyperliquid replies to `activeAssetCtx` subs on the
						// `activeSpotAssetCtx` channel for spot-style assets
						// (outcome coins included). Treat them as equivalent.
						const channelMatches =
							sub.type === channel ||
							(sub.type === 'activeAssetCtx' && channel === 'activeSpotAssetCtx')
						if (!channelMatches) continue
						if (sub.coin && msg.data?.coin !== sub.coin) continue
						if (sub.user && msg.data?.user?.toLowerCase() !== sub.user.toLowerCase()) continue
						for (const l of entry.listeners) l(msg.data)
					}
				} catch {}
			}
		})
		return this.connectPromise
	}

	private startHeartbeat() {
		this.stopHeartbeat()
		this.heartbeatTimer = setInterval(() => {
			if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
			const idle = Date.now() - this.lastMessageAt
			if (idle > HEARTBEAT_FORCE_CLOSE_AFTER_IDLE_MS) {
				// No message (incl. pong) for too long — connection is silently
				// dead. Force-close triggers onclose → reconnect path.
				try {
					this.ws.close()
				} catch {}
				return
			}
			if (idle > HEARTBEAT_PING_AFTER_IDLE_MS) {
				try {
					this.ws.send(JSON.stringify({ method: 'ping' }))
				} catch {
					// send() throws if the socket transitioned to CLOSING/CLOSED
					// since the readyState check above. onclose will fire and
					// reconnect — nothing else to do here.
				}
			}
		}, HEARTBEAT_TICK_MS)
	}

	private stopHeartbeat() {
		if (this.heartbeatTimer) {
			clearInterval(this.heartbeatTimer)
			this.heartbeatTimer = null
		}
	}

	async subscribe(subscription: object, listener: Listener): Promise<() => void> {
		const key = JSON.stringify(subscription)
		await this.connect()
		let entry = this.subs.get(key)
		if (!entry) {
			entry = { count: 0, listeners: new Set(), subscription }
			this.subs.set(key, entry)
			this.ws?.send(JSON.stringify({ method: 'subscribe', subscription }))
		}
		entry.count++
		entry.listeners.add(listener)
		return () => {
			const e = this.subs.get(key)
			if (!e) return
			e.listeners.delete(listener)
			e.count--
			if (e.count <= 0) {
				this.subs.delete(key)
				this.ws?.send(JSON.stringify({ method: 'unsubscribe', subscription }))
			}
		}
	}
}

export type WSStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting'

let singleton: HyperliquidWS | null = null

export function getHyperliquidWS(): HyperliquidWS {
	if (typeof window === 'undefined') {
		throw new Error('HyperliquidWS is browser-only')
	}
	if (!singleton) singleton = new HyperliquidWS()
	return singleton
}
